import * as React from 'react';
import * as styles from './NewsLink.css';
import { LocalizationService } from '../../../services/LocalizationService';
import { inject, observer } from 'mobx-react';
import { Store } from '../stores';
import { IATextField } from '../../textField/TextField';
import MessageService from '../../../services/MessageService';
import { Cover } from '../cover/Cover';
import { IComposerImage } from '../../../interfaces/IComposerImage';
import { Helper } from '../../../Helper';
import { Environment } from '../../../services/Environment';
import { IASkeleton } from '../../skeleton/Skeleton';
import { CategorySelector } from '../categorySelector/CategorySelector';
import { Author } from '../author/Author';

export interface IProps {
  onChange: () => void;
  store?: Store;
}

export interface IState {
  isValidUrl: boolean;
  isLoadingMetaData: boolean;
}

@inject('store')
@observer
export class NewsLink extends React.Component<IProps, IState> {
  private readonly localizationService: LocalizationService = new LocalizationService();

  constructor(props: IProps) {
    super(props);
    this.state = {
      isValidUrl: this.isValidUrl(this.props.store.newsLink),
      isLoadingMetaData: false
    }
    this.localizationService?.checkLocalizedStrings().then(() => this.forceUpdate());
  }

  private isValidUrl(string: string) {
    let url: URL;
    try {
      url = new URL(string);
    } catch (_) {
      return false;  
    }
    return url.protocol === "http:" || url.protocol === "https:";
  }

  public render(): JSX.Element {
    return (
      <div
        className={styles.IA_newsLinkContainer}
      >
        <p>{this.localizationService.strings.Messages_AddNewsLinkTip}</p>
        <IATextField
          onChange={(url) => {
            this.props.store.newsLink = url;
            const isValidUrl = this.isValidUrl(url);
            this.setState({isValidUrl});
            if (isValidUrl) {
              this.setState({ isLoadingMetaData: true });
              MessageService.getLinkMetadata(this.props.store.environment, this.props.store.tenant, url).then(metaData => {
                this.props.store.title.text = metaData?.title;
                this.props.store.cover.image.url = metaData?.imageUrl;
                this.props.store.contentComponents[0] = { type: "text", text: metaData?.description };
              })
              .catch(() => {
                this.props.store.title.text = undefined;
                this.props.store.cover.image.url = undefined;
                this.props.store.contentComponents[0] = { type: "text", text: "" };
              })
              .finally(() => {
                this.props.store.commentsEnabled = false;
                this.setState({isLoadingMetaData: false });
              });
            }
          }}
          text={this.props.store.newsLink}
          borderRadius={5}
          highlightColor={!this.state.isValidUrl && this.props.store.newsLink ? "red" : this.props.store.highlightColor}
          label={"Link"}
          placeholder={this.localizationService.strings.Messages_PasteHere}
          disabled={this.state.isLoadingMetaData}
          style={{
            marginTop: 0,
            float: "unset",
            clear: "unset"
          }}
          inputFieldStyle={{
            float: "unset",
            clear: "unset"
          }}
          labelStyle={{
            color: "grey"
          }}
          onlyPaste
        />
        {this.state.isValidUrl && 
          <>
            {this.state.isLoadingMetaData
            ?
              <>
                {this.getSkeleton()}
              </>
            :
              <>
                <Cover
                  id={this.props.store.cover.id}
                  image={this.props.store.cover.image}
                  video={this.props.store.cover.video}
                  typeToEdit={this.props.store.activeComponent && this.props.store.activeComponent.typeToEdit}
                  hideToolbox={this.props.store.activeComponent && this.props.store.activeComponent.id !== this.props.store.cover.id}
                  showDropZones={this.props.store.componentToAdd != undefined}
                  isMobile={this.props.store.isMobile}
                  onChangeImage={(image: IComposerImage) => {
                    this.props.store.cover.image = image;
                    if(this.props.store.tenantSettings?.admin?.useBlobImages && !this.props.store.isMessagesTemplateEditor){
                      Helper.getImageBlobUrl(Environment.getEnvironmentForImageAPI(this.props.store.environment), this.props.store.tenant, image.url, image.modified, "Full", image?.cropSettings).then((blobUrl)=>{
                        this.props.store.cover.image.imageBlobUrl = blobUrl;
                        this.forceUpdate();
                      })
                    } else {
                      this.forceUpdate();
                    }
                  }}
                  onChangeVideo={() => {}}
                  onFocus={() => {
                    this.props.store.activeComponent = this.props.store.cover;
                  }}
                />
                <CategorySelector
                  style={{
                    margin: 0,
                    width: "100%"
                  }}
                />
                <IATextField
                  onChange={(title) => this.props.store.title.text = title}
                  onFocus={() => { this.props.store.activeComponent = undefined }}
                  text={this.props.store.title.text}
                  borderRadius={5}
                  highlightColor={this.props.store.highlightColor}
                  label={this.localizationService.strings.Composer_Title}
                  placeholder={this.localizationService.strings.Composer_TitlePlaceholderText}
                  labelStyle={{
                    color: "grey"
                  }}
                />
                <Author
                  onChange={() => this.forceUpdate()}
                  style={{
                    margin: 0,
                    width: "100%"
                  }}
                />
                <textarea
                  value={this.props.store.contentComponents[0].text}
                  maxLength={300}
                  onChange={(event) => {
                    if (this.props.store.activeComponent) {
                      this.props.store.contentComponents[0].text = event.target.value;
                      this.props.onChange();
                    }
                  }}
                  onFocus={() => {
                    this.props.store.activeComponent = this.props.store.contentComponents[0];
                  }}
                  className={styles.IA_teaser}
                  style={{
                    border: this.props.store.activeComponent?.type === "text" ? `2px solid ${this.props.store.highlightColor}` : "1px solid rgb(203, 203, 203)"
                  }}
                />
              </>
            }
          </>
        }
      </div>
    );
  }

  private getSkeleton(): React.ReactElement[] {
    return [
      <IASkeleton
        key={0}
        skeleton={{
          borderRadius: 5,
          height: 349,
          width: 620,
          marginTop: 20,
          marginBottom: 10
        }}
      />,
      <IASkeleton
        key={1}
        skeleton={{
          borderRadius: 5,
          height: 36,
          width: 110,
          marginTop: 20
        }}
      />,
      <IASkeleton
        key={2}
        skeleton={{
          borderRadius: 5,
          height: 36,
          width: "100%",
          marginTop: 20
        }}
      />,
      <IASkeleton
        key={3}
        skeleton={{
          borderRadius: 18,
          height: 36,
          width: 135,
          marginTop: 20
        }}
      />,
      <IASkeleton
        key={4}
        skeleton={{
          borderRadius: 5,
          height: 115,
          width: "100%",
          marginTop: 20
        }}
      />
    ]
  }
}